import { useEffect, useId, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Chip, DetailPage, Spinner, Surface } from '@nexvia-logistics/ui';
import { CaretLeft, CurrencyDollar, Storefront, User, type IconProps } from '@nexvia-logistics/ui/icons';
import {
  confirmStorageReception,
  confirmStorageWithdrawal,
  describeStorageError,
  getStorageQuotesMine,
  hasStorageBankAccount,
  reportStorageNotReceived,
  SPACE_TYPE_LABELS,
  submitStorageReview,
  type StorageGuardado,
  type StorageQuote
} from '@/shared/storage-api';
import { formatCurrency, formatDateTime, formatMontevideoDate, formatRelativeTime } from '@/shared/format';
import { CHIP_WRAP } from '@/shared/chip-wrap';
import { KeyValueListStacked } from '@/shared/KeyValueListStacked';
import { QuoteGuardado } from '@/pages/QuoteGuardado';
import { QuoteStatusChip } from '@/pages/QuoteStatusChip';
import { quoteStatusDetail, quoteTitle } from '@/pages/quote-status';
import { ReceptionDialog, type ReceptionAction } from '@/pages/ReceptionDialog';
import { ClientReviewDialog } from '@/pages/ClientReviewDialog';

type LoadState = 'loading' | 'ready' | 'missing' | 'error';

/** NEX-1170 — the quote with the guardado the reception endpoints answered (`data.request`). */
function withGuardado(quote: StorageQuote, guardado: StorageGuardado): StorageQuote {
  if (!quote.request) return quote;
  return {
    ...quote,
    request: {
      ...quote.request,
      status: guardado.status,
      storageStatus: guardado.storageStatus,
      receivedAt: guardado.receivedAt,
      receptionNote: guardado.receptionNote,
      receptionConfirmedBy: guardado.receptionConfirmedBy,
      notReceivedAt: guardado.notReceivedAt,
      notReceivedReason: guardado.notReceivedReason,
      endsAt: guardado.endsAt,
      cancelRequestedAt: guardado.cancelRequestedAt,
      endedAt: guardado.endedAt,
      endedBy: guardado.endedBy,
      withdrawalRequestedAt: guardado.withdrawalRequestedAt,
      // NEX-1329 — the reception opens the review (nexvia-api-web `domain/guardado-review.ts`'s
      // `isReviewEnabled`), so "Calificá al cliente" shows without reloading the page.
      canReview:
        !quote.request.myReview && !!guardado.receivedAt && !!guardado.storageStatus && guardado.storageStatus !== 'contracted'
    }
  };
}

/** NEX-1169 — a quote the client contracted: the one `QuoteGuardado` shows a guardado on. */
function isContracted(quote: StorageQuote): boolean {
  return quote.status === 'accepted' && Boolean(quote.request?.storageStatus);
}

const RECEPTION_NOTICES: Record<ReceptionAction, string> = {
  confirm: 'Confirmaste la recepción. El guardado ya está activo y le avisamos al cliente.',
  'not-received': 'Le avisamos a Nexvia que no recibiste la carga. Se va a comunicar con vos para resolverlo.',
  withdrawal: 'Listo: el guardado terminó y le avisamos al cliente.'
};

/**
 * One card of the page, the client web's shipment detail look (`DetailPanel.tsx`'s `Panel`): a
 * small caption with its icon over a hairline, then the content. The caption is the card's heading,
 * so each card is a section named for screen readers and tests.
 */
function DetailCard({ title, icon: Icon, children }: { title: string; icon: ComponentType<IconProps>; children: ReactNode }) {
  const headingId = useId();
  return (
    <Surface variant="panel" padding="none">
      <section aria-labelledby={headingId}>
        <div className="flex items-center gap-2.5 px-5 py-4 shadow-[inset_0_-1px_0_var(--nx-outline-muted)]">
          <Icon size={18} className="text-primary" />
          <h2 id={headingId} className="m-0 text-ui-caption text-on-surface-variant">
            {title}
          </h2>
        </div>
        <div className="p-5">{children}</div>
      </section>
    </Surface>
  );
}

/**
 * `/cotizaciones/:id` — quote detail page (2026-10-28, Johan: "aumentar a una pantalla por
 * cotización cuando se le da click", like Mis solicitudes in the client web). Mis cotizaciones'
 * rows carry only the summary; everything else about one quote lives here:
 *
 * - the header: "Solicitud #id · size", the status chip and its detail, the client and when it was sent;
 * - "Precio": what the depósito charges and what the client pays, monthly and for the full period,
 *   and how long the quote is valid;
 * - "Tu cotización": the location it went out from, the transfer, the services and the conditions;
 * - "La solicitud": the client, the zone and the space;
 * - the guardado of a contracted quote (`QuoteGuardado`), first, with its reception (decision A4),
 *   withdrawal (X1) and reviews (NEX-1329, decision A6), and — when the depósito has no bank
 *   account saved (NEX-1169) — "Cargá tu cuenta bancaria para que podamos pagarte.".
 *
 * There is no single-quote endpoint for a depósito: the quote comes from the same
 * `GET /storage/quotes/mine` the list reads (as `CotizarPage` reads its request from the inbox). An
 * id that is not there — not this depósito's, or not a number — says so under the way back,
 * never a blank screen.
 */
export default function CotizacionDetailPage() {
  const { id = '' } = useParams();
  const quoteId = Number(id);
  const [state, setState] = useState<LoadState>('loading');
  const [quote, setQuote] = useState<StorageQuote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reception, setReception] = useState<ReceptionAction | null>(null);
  // NEX-1329 — "Calificá al cliente" is open.
  const [reviewing, setReviewing] = useState(false);
  // NEX-1169 — true only once the API said there is no bank account; unknown (not asked, or the
  // call failed) shows nothing rather than a notice that may be wrong.
  const [missingBankAccount, setMissingBankAccount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Another id under the same route: never leave the previous quote, its notices or its dialogs
    // up. The list's scroll position is not this page's either (BrowserRouter keeps it): a row far
    // down the list would open the quote mid-page, title and way back out of sight.
    window.scrollTo({ top: 0 });
    setState('loading');
    setQuote(null);
    setError(null);
    setNotice(null);
    setReception(null);
    setReviewing(false);
    setMissingBankAccount(false);

    getStorageQuotesMine().then(
      (quotes) => {
        if (cancelled) return;
        const found = quotes.find((item) => item.id === quoteId) ?? null;
        setQuote(found);
        setState(found ? 'ready' : 'missing');
        // NEX-1169 — asked only when a guardado is there to be paid for; never blocks the page.
        if (found && isContracted(found)) {
          hasStorageBankAccount()
            .then((hasAccount) => {
              if (!cancelled) setMissingBankAccount(!hasAccount);
            })
            .catch(() => undefined);
        }
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(describeStorageError(err, 'No pudimos cargar la cotización.'));
        setState('error');
      }
    );
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  const openReception = (action: ReceptionAction) => {
    setNotice(null);
    setReception(action);
  };

  // Throws to the dialog on failure, which shows the error and stays open.
  const handleReception = async (text: string | null) => {
    if (!reception || !quote) return;
    const guardado =
      reception === 'confirm'
        ? await confirmStorageReception(quote.storageRequestId, text)
        : reception === 'withdrawal'
          ? await confirmStorageWithdrawal(quote.storageRequestId)
          : await reportStorageNotReceived(quote.storageRequestId, text ?? '');
    setQuote((prev) => (prev ? withGuardado(prev, guardado) : prev));
    setNotice(RECEPTION_NOTICES[reception]);
    setReception(null);
  };

  const openReview = () => {
    setNotice(null);
    setReviewing(true);
  };

  // NEX-1329 — throws to the dialog on failure, which shows the error and stays open. Once saved,
  // the depósito's own review replaces "Calificá al cliente" (one per side, decision A6).
  const handleReview = async (input: { rating: number; comment: string | null }) => {
    if (!quote) return;
    const { id: reviewId, rating, comment, createdAt } = await submitStorageReview(quote.storageRequestId, input);
    setQuote((prev) =>
      prev?.request
        ? { ...prev, request: { ...prev.request, canReview: false, myReview: { id: reviewId, rating, comment, createdAt } } }
        : prev
    );
    setReviewing(false);
    setNotice('Gracias. Tu calificación del cliente quedó guardada.');
  };

  // DetailPage has no slot above its title: `order-first` lifts the link there visually (the page is
  // a flex column) while it stays after the heading in the DOM — the same way back as `CotizarPage`.
  const backLink = (
    <Link
      to="/cotizaciones"
      className="order-first inline-flex min-h-11 items-center gap-1 self-start rounded-card text-ui-label text-primary no-underline hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <CaretLeft size={14} />
      Volver a mis cotizaciones
    </Link>
  );

  if (state !== 'ready' || !quote) {
    return (
      <DetailPage title="Cotización" navigation={backLink}>
        {state === 'loading' && (
          <p className="m-0 flex items-center gap-2 text-ui-body text-on-surface-variant">
            <Spinner size={18} label={null} />
            Cargando la cotización…
          </p>
        )}
        {state === 'error' && <Alert tone="critical">{error}</Alert>}
        {state === 'missing' && (
          <Alert tone="info">
            No encontramos esta cotización entre las de tu depósito. Revisá que hayas entrado con la cuenta del depósito que
            la envió.
          </Alert>
        )}
      </DetailPage>
    );
  }

  const statusDetail = quoteStatusDetail(quote);
  // NEX-1165 (W2-hard-a) — each service's label comes with the quote (`includedServiceLabels`: the
  // catalog label, inactive rows included; free text is its own label). A value with no label shows as sent.
  const serviceLabels = new Map((quote.includedServiceLabels ?? []).map((item) => [item.code, item.label]));
  const request = quote.request;

  const priceItems = [
    { term: 'Vos cobrás por mes', value: formatCurrency(quote.monthlyPrice, quote.currency) },
    { term: 'Vos cobrás el período completo', value: formatCurrency(quote.fullPeriodPrice, quote.currency) },
    // Blocker #1 (original build report) — the marked-up price the client actually pays, fees
    // included, already computed by the API on every quote.
    { term: 'El cliente paga por mes', value: formatCurrency(quote.monthlyClientAmount, quote.currency) },
    {
      term: 'El cliente paga el período completo',
      // An open-ended request has no full period to price for the client.
      value: quote.fullPeriodClientAmount !== null ? formatCurrency(quote.fullPeriodClientAmount, quote.currency) : '—'
    },
    ...(quote.validUntil ? [{ term: 'Válida hasta', value: formatMontevideoDate(quote.validUntil) }] : [])
  ];

  const offerItems = [
    // Audit S4 — which of the depósito's locations the quote went out from. `null`: sent before
    // NEX-1165 made it required, or the location was deleted since.
    ...(quote.location
      ? [
          {
            term: 'Depósito',
            value: `${quote.location.address} — ${quote.location.locality}, ${quote.location.department}`
          }
        ]
      : []),
    // Audit S3 — the same words as QuoteForm's transfer checkbox.
    { term: 'Traslado', value: quote.transportIncluded ? 'Incluye el traslado hasta el depósito' : 'No incluye el traslado' },
    ...(quote.includedServices.length > 0
      ? [
          {
            term: 'Servicios incluidos',
            value: (
              <span className="flex flex-wrap gap-1.5">
                {quote.includedServices.map((service) => (
                  <Chip key={service} tone="neutral" className={CHIP_WRAP}>
                    {serviceLabels.get(service) ?? service}
                  </Chip>
                ))}
              </span>
            )
          }
        ]
      : []),
    // NEX-1165 — one condition per line (QuoteForm sends each catalog condition as its own line,
    // after the API's plazo/preaviso lines): a plain paragraph would run them together.
    ...(quote.conditions
      ? [
          {
            term: 'Condiciones',
            value: quote.conditions.split('\n').map((line, index) => (
              <span key={index} className="block">
                {line}
              </span>
            ))
          }
        ]
      : []),
    { term: 'Enviada el', value: formatDateTime(quote.createdAt) }
  ];

  const requestItems = request
    ? [
        ...(request.clientName ? [{ term: 'Cliente', value: request.clientName }] : []),
        { term: 'Zona', value: `${request.locality}, ${request.department}` },
        { term: 'Espacio', value: SPACE_TYPE_LABELS[request.spaceType] ?? request.spaceType }
      ]
    : [];

  return (
    <DetailPage
      title={quoteTitle(quote)}
      description={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <QuoteStatusChip quote={quote} />
          <span>
            {[statusDetail, request?.clientName, `enviada ${formatRelativeTime(quote.createdAt)}`].filter(Boolean).join(' · ')}
          </span>
        </span>
      }
      navigation={backLink}
      notice={notice ? <Alert tone="positive">{notice}</Alert> : undefined}
    >
      <QuoteGuardado
        quote={quote}
        surface="panel"
        missingBankAccount={missingBankAccount}
        onConfirmReception={() => openReception('confirm')}
        onReportNotReceived={() => openReception('not-received')}
        onConfirmWithdrawal={() => openReception('withdrawal')}
        onReviewClient={openReview}
      />

      {/* The price and the offer side by side from lg, like the client web's shipment page (route |
          contents); on a phone they follow the guardado, which is what a depósito arriving from the
          mail came to act on. */}
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
        <DetailCard title="Precio" icon={CurrencyDollar}>
          <KeyValueListStacked items={priceItems} />
          <p className="m-0 mt-3 text-ui-small text-on-surface-variant">
            Nexvia intermedia el cobro: el fee se monta sobre tu precio.
          </p>
        </DetailCard>

        <DetailCard title="Tu cotización" icon={Storefront}>
          <KeyValueListStacked items={offerItems} />
        </DetailCard>
      </div>

      {requestItems.length > 0 && (
        <DetailCard title="La solicitud" icon={User}>
          <KeyValueListStacked items={requestItems} />
        </DetailCard>
      )}

      {reception && (
        <ReceptionDialog
          key={reception}
          action={reception}
          onSubmit={handleReception}
          onCancel={() => setReception(null)}
        />
      )}

      {reviewing && <ClientReviewDialog onSubmit={handleReview} onCancel={() => setReviewing(false)} />}
    </DetailPage>
  );
}
